import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import api from 'services';

import { addNotification } from 'actions/notifications/notifications';
import { updateArea } from 'actions/regions/areas';

import { execute } from 'store/configureStore';
import { getAreas } from 'sagas/regions/areas';
import { getPorts } from 'sagas/regions/ports';

import MergingAreas from './Areas';
import MergingPorts from './Ports';
import MergeErrorDialog from './MergeErrorDialog';

/**
 * MergingPage Component
 *
 * @param {Object} props
 * @param {Function} props.addNotification
 * @param {Function} props.updateArea
 * @param {Object} [props.area]
 * @param {Boolean} props.isUpdating
 * @param {Function} [props.createNewArea]
 */
const MergingPage = (props) => {
    const {
        area,
        isUpdating,
        createNewArea,
    } = props;

    const [sourceArea, setSourceArea] = useState({});
    const [destinationArea, setDestinationArea] = useState({});
    const [sourcePort, setSourcePort] = useState({});
    const [destinationPort, setDestinationPort] = useState({});

    const [disabledMergePorts, setDisabledMergePorts] = useState(true);

    const [clearAreas, setClearAreas] = useState(false);
    const [clearPorts, setClearPorts] = useState(false);
    const [errorAltNames, setErrorAltNames] = useState([]);
    const [isMergeErrorOpened, setIsMergeErrorOpened] = useState(false);
    const [isUpdatingState, setIsUpdatingState] = useState(false);

    useEffect(() => {
        if (sourcePort._id && destinationPort._id) {
            setDisabledMergePorts(false);
        } else {
            setDisabledMergePorts(true);
        }
    }, [sourcePort._id, destinationPort._id]);

    useEffect(() => {
        if (area && area._id && sourceArea._id !== area._id) {
            api.findPortAdmin({ name: area.name, onlyAreasWithChild: true })
                .then((response) => {
                    if (response.status === 200) {
                        let sourceObject = { name: '' };

                        response.data.data.forEach((item) => {
                            if (item._id === area._id) {
                                sourceObject = item;
                            }
                        });

                        setSourcePort(sourceObject);
                        setSourceArea(sourceObject);
                    }
                });
        }
    }, [area, sourceArea._id]);

    useEffect(() => {
        if (isUpdating !== isUpdatingState) {
            setIsUpdatingState(isUpdating);
            setDestinationArea({});
            if (!isUpdating) {
                setSourceArea({});
            }
            setClearAreas(isUpdating);
        }
    }, [isUpdating, isUpdatingState]);

    useEffect(() => {
        const state = props.location && props.location.state;
        if (state && state.sourceArea && state.destination) {
            const { sourceArea: stateSource, destination: stateDest, isPort } = state;

            // set initial state name so the auto-completes immediately display the text while loading the full objects
            if (isPort) {
                setSourcePort({ name: stateSource.name });
                setDestinationPort({ name: stateDest.name });
            } else {
                setSourceArea({ name: stateSource.name });
                setDestinationArea({ name: stateDest.name });
            }

            if (isPort) {
                api.findPortAdmin({ name: stateSource.name, withAreasWithChild: true, withUserPorts: true })
                    .then((response) => {
                        if (response.status === 200) {
                            const fullSource = response.data.data.find(item => item._id === stateSource._id);
                            if (fullSource) {
                                setSourcePort(fullSource);
                            }
                        }
                    });
            } else {
                api.findPortAdmin({ name: stateSource.name, onlyAreasWithChild: true })
                    .then((response) => {
                        if (response.status === 200) {
                            const fullSource = response.data.data.find(item => item._id === stateSource._id);
                            if (fullSource) {
                                setSourceArea(fullSource);
                            }
                        }
                    });
            }

            if (isPort) {
                api.getPort({ id: stateDest._id })
                    .then((response) => {
                        if (response && response.status === 200 && response.data) {
                            setDestinationPort(response.data);
                        } else if (response && response._id) {
                            setDestinationPort(response);
                        }
                    });
            } else {
                api.findPortAdmin({ name: stateDest.name, onlyAreasWithChild: true })
                    .then((response) => {
                        if (response.status === 200) {
                            const fullDest = response.data.data.find(item => item._id === stateDest._id);
                            if (fullDest) {
                                setDestinationArea(fullDest);
                                return;
                            }
                        }

                        // fallback if not found in list or response was not 200
                        api.getArea(stateDest._id)
                            .then((res) => {
                                if (res && res.status === 200 && res.data) {
                                    setDestinationArea(res.data);
                                } else if (res && res._id) {
                                    setDestinationArea(res);
                                }
                            });
                    })
                    .catch(() => {
                        api.getArea(stateDest._id)
                            .then((res) => {
                                if (res && res.status === 200 && res.data) {
                                    setDestinationArea(res.data);
                                } else if (res && res._id) {
                                    setDestinationArea(res);
                                }
                            });
                    });
            }
        }
    }, [props.location]);

    const addValue = (type, valueObject) => {
        switch (type) {
        case 'sourceArea':
            setSourceArea(valueObject);
            break;
        case 'destinationArea':
            setDestinationArea(valueObject);
            break;
        case 'sourcePort':
            setSourcePort(valueObject);
            break;
        case 'destinationPort':
            setDestinationPort(valueObject);
            break;
        default:
            break;
        }
    };

    const updateAreas = async () => {
        await execute(getAreas);
        if (createNewArea) {
            createNewArea();
        }
    };

    const updateAreasAndPorts = async () => {
        await execute(getAreas);
        await execute(getPorts);
        if (createNewArea) {
            createNewArea();
        }
    };

    const mergePorts = () => {
        setDisabledMergePorts(true);
        api.mergeAreaOrPortToPort(destinationPort._id, sourcePort._id)
            .then((response) => {
                setSourcePort({});
                setDestinationPort({});
                setDisabledMergePorts(true);
                setClearPorts(true);

                props.addNotification({
                    title: 'Success',
                    message: response.data.data.message,
                    level: 'success',
                    _id: new Date(),
                    autoDismiss: 2,
                });

                setClearPorts(false);
                if (createNewArea) {
                    updateAreasAndPorts();
                }
            })
            .catch((error) => {
                setDisabledMergePorts(false);
                props.addNotification({
                    title: 'Error',
                    message: error.response.data.errors[0].messages[0],
                    level: 'error',
                    _id: new Date(),
                    autoDismiss: 10,
                });
            });
    };

    const handleMergeErrorOpened = () => {
        setIsMergeErrorOpened(prev => !prev);
    };

    const mergeAreas = () => {
        api.mergeAreaToArea(destinationArea._id, sourceArea._id)
            .then((response) => {
                setSourceArea({});
                setDestinationArea({});
                setClearAreas(true);

                props.addNotification({
                    title: 'Success',
                    message: response.data.data.message,
                    level: 'success',
                    _id: new Date(),
                    autoDismiss: 2,
                });

                setClearAreas(false);
                setErrorAltNames([]);

                if (createNewArea) {
                    updateAreas();
                }
            })
            .catch((error) => {
                const message = error.response.data.errors[0].messages[0];
                props.addNotification({
                    title: 'Error',
                    message,
                    level: 'error',
                    _id: new Date(),
                    autoDismiss: 10,
                });

                if (/area already exists with this alt names/i.test(message)) {
                    let alts = [];

                    if (error.response.data.names) {
                        alts = error.response.data.names.map(n => n.toLowerCase());
                    }

                    setErrorAltNames(alts);
                    setIsMergeErrorOpened(true);
                }
            });
    };

    const addAreaToArea = () => {
        props.updateArea({
            _id: destinationArea._id,
            name: destinationArea.name,
            childs: [
                ...destinationArea.childs.map(item => item._id),
                sourceArea._id,
            ],
        });
    };

    const joinAreas = () => {
        api.joinAreaToArea(destinationArea._id, sourceArea._id)
            .then((response) => {
                setSourceArea({});
                setDestinationArea({});
                setClearAreas(true);

                props.addNotification({
                    title: 'Success',
                    message: response.data.data.message,
                    level: 'success',
                    _id: new Date(),
                    autoDismiss: 2,
                });

                setClearAreas(false);

                if (createNewArea) {
                    updateAreas();
                }
            })
            .catch((error) => {
                props.addNotification({
                    title: 'Error',
                    message: error.response.data.errors[0].messages[0],
                    level: 'error',
                    _id: new Date(),
                    autoDismiss: 10,
                });
            });
    };

    const handleBypassError = async () => {
        try {
            const areaInfo = await api.getArea(destinationArea._id);
            if (areaInfo.status !== 200) {
                props.addNotification({
                    title: 'Error',
                    message: 'Destination area not found',
                    level: 'error',
                    _id: new Date(),
                    autoDismiss: 10,
                });
                return;
            }

            const updatedAltsArea = await api.updateAreaAlt({
                _id: destinationArea._id,
                alt: areaInfo.data.alt
                    .filter(a => !errorAltNames.includes(a.toLowerCase())),
            });

            if (updatedAltsArea.status !== 200) {
                props.addNotification({
                    title: 'Error',
                    message: updatedAltsArea.data.errors[0].messages[0],
                    level: 'error',
                    _id: new Date(),
                    autoDismiss: 10,
                });
                return;
            }

            mergeAreas();
            handleMergeErrorOpened();
        } catch (e) {
            props.addNotification({
                title: 'Error',
                message: 'Could not merge areas',
                level: 'error',
                _id: new Date(),
                autoDismiss: 10,
            });
        }
    };

    const areaButtonsDisabled = !destinationArea._id || !sourceArea._id || isUpdating;

    return (
        <div id="merging" className="container" style={{ maxWidth: '100%' }}>
            <MergeErrorDialog
                errorAltNames={errorAltNames}
                handleBypassError={handleBypassError}
                handleMergeErrorOpened={handleMergeErrorOpened}
                isMergeErrorOpened={isMergeErrorOpened}
            />
            <MergingAreas
                mergeAreas={mergeAreas}
                joinAreas={joinAreas}
                addAreaToArea={addAreaToArea}
                addValue={addValue}
                disabledBtn={areaButtonsDisabled}
                disabledAddAreaToArea={areaButtonsDisabled}
                clearAreas={clearAreas}
                sourceArea={sourceArea}
                destinationArea={destinationArea}
            />

            <MergingPorts
                mergePorts={mergePorts}
                addValue={addValue}
                disabledBtn={disabledMergePorts}
                clearPorts={clearPorts}
                sourcePort={sourcePort}
                destinationPort={destinationPort}
            />
        </div>
    );
};

export default connect(
    ({ user: { isLogged }, areas }, { area }) => ({
        isLogged,
        area,
        isUpdating: areas.isUpdating,
    }),
    dispatch => bindActionCreators(
        {
            addNotification,
            updateArea,
        },
        dispatch,
    ),
)(MergingPage);



// WEBPACK FOOTER //
// ./app/components/Regions/Merging/Page.jsx